/**
 * PDP Wishlist Popup Handler
 * Handles custom wishlist functionality with variant selection popup
 * Uses Swym API for wishlist operations
 * 
 * Behavior:
 * - If no size is selected: show popup to select variant
 * - If size is selected AND not wishlisted: add directly to wishlist (no popup)
 * - If size is selected AND already wishlisted: show popup for removal
 */
(function() {
  'use strict';

  // Custom Wishlist Button Handler - Button only opens popup
  var wishlistBtn = document.getElementById('pdp-wishlist-btn');
  var wishlistPopup = document.getElementById('wishlist-variant-popup');
  var productWishlistData = []; // Store wishlisted variants for this product

  // Ensure Swym visitor/session registration is complete before write calls.
  function withSwymReady(onReady, onError) {
    var maxAttempts = 25;
    var attempt = 0;

    function run() {
      attempt += 1;

      if (!window._swat || typeof window._swat.fetch !== 'function') {
        if (attempt < maxAttempts) return setTimeout(run, 120);
        if (typeof onError === 'function') onError(new Error('Swym not available'));
        return;
      }

      window._swat.fetch(function() {
        onReady(window._swat);
      }, function(err) {
        if (attempt < maxAttempts) return setTimeout(run, 120);
        if (typeof onError === 'function') onError(err || new Error('Swym not ready'));
      });
    }

    run();
  }

  // Exit early if elements don't exist
  if (!wishlistBtn || !wishlistPopup) return;

  // Get currently selected variant ID from the product form
  function getSelectedVariantId() {
    // Check the product select dropdown first
    var productSelect = document.getElementById('productSelect');
    if (productSelect && productSelect.value) {
      return parseInt(productSelect.value);
    }
    return null;
  }

  // Check if a size swatch is currently selected
  function isSizeSelected() {
    var sizeSwatch = document.querySelector('.swatch[data-swatch="size"]');
    if (!sizeSwatch) return true; // If no size swatch exists, consider it "selected"
    
    var checkedInput = sizeSwatch.querySelector('input[type="radio"]:checked');
    return checkedInput !== null;
  }

  // Check if a specific variant is already in wishlist
  function isVariantWishlisted(variantId) {
    return productWishlistData.some(function(item) {
      return item.epi === variantId;
    });
  }

  // Update main wishlist button to show active state based on selected variant
  function updateMainButtonState() {
    if (!wishlistBtn) return;
    
    var selectedVariantId = getSelectedVariantId();
    
    // If a variant is selected, show heart state based on that variant
    // Otherwise, show filled heart if ANY variant is wishlisted
    var isActive = false;
    if (selectedVariantId && isSizeSelected()) {
      isActive = isVariantWishlisted(selectedVariantId);
    } else {
      isActive = productWishlistData.length > 0;
    }
    
    wishlistBtn.classList.toggle('swym-added', isActive);
    wishlistBtn.classList.toggle('wishlisted', isActive);

    // Convey toggle state to screen readers (Allyant #20)
    wishlistBtn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  }

  // Update variant buttons in popup to show wishlisted state
  function updatePopupVariantStates() {
    if (!wishlistPopup) return;
    
    var variantOptions = wishlistPopup.querySelectorAll('.wishlist-variant-popup__option');
    variantOptions.forEach(function(option) {
      var variantId = parseInt(option.dataset.variantId);
      var isAvailable = option.dataset.variantAvailable === 'true';
      var isWishlisted = productWishlistData.some(function(item) {
        return item.epi === variantId;
      });
      
      option.classList.toggle('wishlisted', isWishlisted);
      option.setAttribute('aria-pressed', isWishlisted ? 'true' : 'false');

      // Show unavailable variants only if they are wishlisted
      if (!isAvailable) {
        option.style.display = isWishlisted ? 'flex' : 'none';
      }
    });
    
    // Also update main button state
    updateMainButtonState();
  }

  // Fetch and store wishlist data for this product
  function fetchProductWishlistData(swat, callback) {
    if (!wishlistBtn) return;
    var productId = parseInt(wishlistBtn.dataset.productId);
    
    swat.fetch(function(wishlist) {
      productWishlistData = wishlist.filter(function(item) {
        return item.empi === productId;
      });
      if (callback) callback();
    });
  }

  // Add variant to wishlist (can be called with or without variantOption element)
  function addVariantToWishlist(variantId, variantOption, callback) {
    var productId = parseInt(wishlistBtn.dataset.productId);
    var productUrl = wishlistBtn.dataset.productUrl;
    var productTitle = wishlistBtn.dataset.productTitle;
    var productImage = wishlistBtn.dataset.productImage;
    var productPrice = parseFloat(wishlistBtn.dataset.productPrice) || 0;

    var wishlistItem = {
      epi: parseInt(variantId),
      empi: productId,
      du: productUrl,
      dt: productTitle,
      iu: productImage,
      pr: productPrice
    };

    withSwymReady(function(swat) {
      swat.addToWishList(
        wishlistItem,
        function(response) {
          console.log('Added to wishlist:', response);
          // Update local data and UI
          productWishlistData.push(wishlistItem);
          if (variantOption) {
            variantOption.classList.add('wishlisted');
            variantOption.setAttribute('aria-pressed', 'true');
          }
          // Update main button state
          updateMainButtonState();
          if (callback) callback();
        },
        function(error) {
          console.error('Error adding to wishlist:', error);
        }
      );
    }, function(error) {
      console.error('Swym not ready for addToWishList:', error);
    });
  }

  // Remove variant from wishlist
  function removeVariantFromWishlist(variantId, variantOption) {
    if (!window._swat) return;
    
    var item = productWishlistData.find(function(i) {
      return i.epi === parseInt(variantId);
    });
    
    if (item) {
      window._swat.removeFromWishList(
        { empi: item.empi, epi: item.epi },
        function() {
          console.log('Removed from wishlist');
          // Update local data and UI
          productWishlistData = productWishlistData.filter(function(i) {
            return i.epi !== parseInt(variantId);
          });
          variantOption.classList.remove('wishlisted');
          variantOption.setAttribute('aria-pressed', 'false');
          // Update main button state
          updateMainButtonState();
        }
      );
    }
  }

  // Get all focusable elements inside a container
  function getFocusableElements(container) {
    var elements = container.querySelectorAll(
      'button:not([disabled]):not([style*="display: none"]):not([style*="display:none"]), ' +
      '[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), ' +
      '[tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(elements, function(el) {
      return el.offsetParent !== null; // visible check
    });
  }

  // Trap focus inside the popup
  function handlePopupKeydown(e) {
    if (e.key === 'Escape') {
      closeWishlistPopup();
      return;
    }
    if (e.key !== 'Tab') return;

    var focusable = getFocusableElements(wishlistPopup);
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // Show popup and refresh wishlist states
  function showWishlistPopup() {
    if (wishlistPopup) {
      // Refresh wishlist data before showing
      if (window._swat) {
        fetchProductWishlistData(window._swat, function() {
          updatePopupVariantStates();
        });
      }
      wishlistPopup.style.display = 'flex';
      document.body.style.overflow = 'hidden';

      // Move focus into the popup
      setTimeout(function() {
        var closeBtn = wishlistPopup.querySelector('.wishlist-variant-popup__close');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      }, 50);

      // Add keyboard listener for focus trap and Escape
      document.addEventListener('keydown', handlePopupKeydown);
    }
  }

  // Close popup
  function closeWishlistPopup() {
    if (wishlistPopup) {
      wishlistPopup.style.display = 'none';
      document.body.style.overflow = '';

      // Remove keyboard listener
      document.removeEventListener('keydown', handlePopupKeydown);

      // Return focus to the wishlist button that opened the popup. preventScroll: iOS lets the page
      // scroll behind the popup, and a plain focus() snapped it back to the button on close.
      if (wishlistBtn) wishlistBtn.focus({ preventScroll: true });
    }
  }

  // Wishlist button click handler - Smart behavior based on variant selection
  wishlistBtn.addEventListener('click', function(e) {
    e.preventDefault();
    e.stopPropagation();
    
    var selectedVariantId = getSelectedVariantId();
    var sizeSelected = isSizeSelected();
    
    // If no size is selected, always show popup to select variant
    if (!sizeSelected || !selectedVariantId) {
      showWishlistPopup();
      return;
    }
    
    // Size is selected - check if variant is already wishlisted
    var isWishlisted = isVariantWishlisted(selectedVariantId);
    
    if (isWishlisted) {
      // Variant is already wishlisted - show popup for removal
      showWishlistPopup();
    } else {
      // Variant is NOT wishlisted - add directly without popup
      addVariantToWishlist(selectedVariantId, null, function() {
        // Optional: Show a brief feedback (e.g., animate the heart)
        console.log('Variant added to wishlist directly');
      });
    }
  });

  // Popup close handlers
  var closeBtn = wishlistPopup.querySelector('.wishlist-variant-popup__close');
  var overlay = wishlistPopup.querySelector('.wishlist-variant-popup__overlay');
  
  if (closeBtn) {
    closeBtn.addEventListener('click', closeWishlistPopup);
  }
  if (overlay) {
    overlay.addEventListener('click', closeWishlistPopup);
  }

  // Variant option click handler - toggle add/remove
  var variantOptions = wishlistPopup.querySelectorAll('.wishlist-variant-popup__option');
  variantOptions.forEach(function(option) {
    option.addEventListener('click', function() {
      var variantId = this.dataset.variantId;
      var isWishlisted = this.classList.contains('wishlisted');
      
      if (isWishlisted) {
        removeVariantFromWishlist(variantId, this);
      } else {
        addVariantToWishlist(variantId, this);
      }
    });
  });

  // Initialize when Swym is ready
  if (!window.SwymCallbacks) {
    window.SwymCallbacks = [];
  }
  window.SwymCallbacks.push(function(swat) {
    fetchProductWishlistData(swat, updatePopupVariantStates);
  });

  // Listen for variant changes to update button state
  var productSelect = document.getElementById('productSelect');
  if (productSelect) {
    productSelect.addEventListener('change', function() {
      updateMainButtonState();
    });
  }
  
  // Also listen for swatch clicks to update button state
  var sizeSwatch = document.querySelector('.swatch[data-swatch="size"]');
  if (sizeSwatch) {
    sizeSwatch.addEventListener('change', function() {
      // Small delay to ensure variant select is updated
      setTimeout(updateMainButtonState, 100);
    });
  }
  
  // Listen for custom variant change event (dispatched by the theme)
  window.addEventListener('tn-variant-changed', function() {
    setTimeout(updateMainButtonState, 100);
  });

  // Hide Swym's default button since we have our own, and remove gap from wrapper
  var style = document.createElement('style');
  style.textContent = '#swym-atw-pdp-button { display: none !important; } .swym-atw-btn-wrapper.add_to_cart_button_right-position { gap: 0 !important; }';
  document.head.appendChild(style);

})();
